import { fromPairs, omit } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useController } from '../hooks/useController.ts';
import { useInputController } from '../hooks/useInputController.ts';
import { useFormItemScreenReaderLabelAddition } from '../item/label/useFormItemScreenReaderLabelAddition.tsx';
import { useFormItemAccessibility } from '../item/useFormItemAccessibility.tsx';
import { useFormItemEnabled } from '../item/useFormItemEnabled.ts';
import { extractValueOptionList } from '../utils/extractValueOptionList.ts';
import { type ISelectInputProps } from './SelectInput.tsx';
import { renderSelectOptions } from './select/renderSelectOptions.tsx';
import { Select } from './select/Select.tsx';
import { SelectReadOnly } from './select/SelectReadOnly.tsx';
import { useFilterSelectValuesOnOptionsChange } from './select/useFilterSelectValuesOnOptionsChange.ts';
import { useSelectedOptionLabel } from './select/useSelectedOptionLabel.tsx';

export interface ISelectObjectInputProps extends ISelectInputProps {
  // Supportiamo sole le multiple come object
  mode: 'multiple';
  /**
   * Abbiamo bisogno di registrare la `ref` del campo "padre" (il gruppo di
   * opzioni) per poter visualizzare correttamente gli errori di validazione.
   *
   * Questo perché RHF fa il focus sul campo padre (es. `scelta_multipla`), e
   * non sul campo figlio (es. `scelta_multipla_options@internal`).
   */
  registerAsRef?: string;
}

/**
 * Permette di gestire una select come _oggetto_, esclusivamente in caso
 * di select multipla (per la singola non ha senso)
 */
export function SelectObjectInput(props: ISelectObjectInputProps) {
  const {
    name,
    options,
    renderOption,
    children,
    onChange: onCustomChange,
    style,
    readOnly,
    group,
    registerAsRef,
    ...inputProps
  } = props;

  const { enabled, visuallyDisabled } = useFormItemEnabled();

  if (process.env.NODE_ENV === 'development' && !registerAsRef) {
    console.warn(
      'SelectObjectInput: `registerAsRef` non specificato, non sarà possibile mostrare gli errori di validazione correttamente. Assicurarsi che il campo sia registrato correttamente e che "useController" non crei errori con il FormHandler.',
    );
  }
  const {
    field: { ref: registerRef },
  } = useController(registerAsRef ?? '<unknown>');

  const {
    field: { value: objectValue, onChange: onObjectChange, onBlur, ref },
  } = useInputController(name, { enabled });

  /**
   * Mappa fra oggetto { A: true, B: true } e array [A, B]
   */
  const value = useMemo(() => {
    if (!objectValue) return [];
    return Object.entries(objectValue)
      .filter(([key, selected]) => selected === true)
      .map(([key, _]) => key);
  }, [objectValue]);

  /**
   * Conversione fra array [A, B] in oggetto { A: true, B: true }
   */
  const onChange = useCallback(
    (items: any[] | null | undefined) => {
      // Fondamentale che ci sia `withDisabled` per preservare tutte le chiavi
      // anche delle opzioni disabilitate.
      const optionListValues = extractValueOptionList(options, {
        withDisabled: true,
      });
      const nextValue = fromPairs(optionListValues.map(opt => [opt, false]));

      if (!items) return onObjectChange(nextValue);

      for (const optionValue of items) {
        nextValue[optionValue] = true;
      }

      return onObjectChange(nextValue);
    },
    [options, onObjectChange],
  );

  useFilterSelectValuesOnOptionsChange({
    mode: 'multiple',
    options,
    value,
    onChange,
  });

  const filterProp = inputProps.optionFilterProp ?? 'label';

  const selectedOptions = useSelectedOptionLabel(
    value,
    options,
    'multiple',
    filterProp,
  );

  // Aggiungo un label nascosto per l'accessibilità, che indica il valore
  // selezionato.
  useFormItemScreenReaderLabelAddition(readOnly ? null : selectedOptions);

  const accessibility = useFormItemAccessibility();

  if (readOnly) {
    return (
      <SelectReadOnly
        value={value}
        options={options ?? []}
        filterProp={filterProp}
        disabled={visuallyDisabled}
      />
    );
  }

  return (
    <Select
      size="large"
      style={{ textAlign: 'left', ...style }}
      disabled={visuallyDisabled}
      // Le Select non supportano aria-required, errore di validazione Axe
      {...omit(accessibility.attributes, ['aria-required'])}
      popupMatchSelectWidth={false}
      {...inputProps}
      mode="multiple"
      ref={elm => {
        ref(elm);
        registerRef(elm);
      }}
      value={value ?? []}
      onChange={(value, option) => {
        onCustomChange
          ? onCustomChange(value, option, onChange)
          : onChange(value);
      }}
      onBlur={onBlur}
    >
      {renderSelectOptions(group, options, renderOption)}
      {children}
    </Select>
  );
}
